/* DigiGuide Import / Export – Home / Selector page (UPDATED 2025‑06‑17‑d)
   -----------------------------------------------------------------------------
   ● Typing lag gone (uncontrolled <Input>)
   ● Set‑Token button live‑enables via hasText
   ● Fatal‑token clearing now waits for the **new** query to finish – solves the
     loop where a *valid* token was being cleared because the old error still
     lingered in Apollo’s cache.
*/

import * as React from "react";
import { useLocation, navigate } from "@reach/router";
import { useQuery } from "@apollo/client";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Input from "@mui/material/Input";
import Stack from "@mui/material/Stack";

import { Text } from "@base";
import importClient from "../apollo/importClient";
import ManualSelector from "../components/importexport/ManualSelector";
import ImportHeader from "../components/importexport/ImportHeader";
import QUERY_MANUAL_LIST_ALL_SITES from "../craft/dynamicQueries/QUERY_MANUAL_LIST_ALL_SITES";

const SELECT_PATH = "/importexport/select";

export default function ImportExportHome() {
  const location = useLocation();
  const qsToken =
    new URLSearchParams(location.search).get("CRAFT_API_IMPORTEXPORT_TOKEN") ||
    new URLSearchParams(location.search).get("token") ||
    new URLSearchParams(location.search).get("x-craft-live-preview") ||
    null;
  const stored =
    typeof window !== "undefined"
      ? localStorage.getItem("GATSBY_CRAFT_API_EXPORT_TOKEN") || ""
      : "";

  const tokenRef = React.useRef(null);
  const [hasAuth, setAuth] = React.useState(Boolean(stored || qsToken));
  const [hasText, setHasText] = React.useState(Boolean(stored || qsToken));
  const [fatalErr, setFatal] = React.useState(null);

  /* ------------------------------------------------------------------ */
  /*  bootstrap from query‑string token                                 */
  /* ------------------------------------------------------------------ */
  React.useEffect(() => {
    if (qsToken) {
      localStorage.setItem("GATSBY_CRAFT_API_EXPORT_TOKEN", qsToken);
      setAuth(true);
      navigate(SELECT_PATH, { replace: true });
    }
  }, [qsToken]);

  /* redirect to /select if we already have a token */
  React.useEffect(() => {
    if (hasAuth && location.pathname === "/importexport") {
      navigate(SELECT_PATH, { replace: true });
    }
  }, [hasAuth, location.pathname]);

  /* Apollo query – skipped until hasAuth === true */
  const { data, loading, error } = useQuery(QUERY_MANUAL_LIST_ALL_SITES, {
    client: importClient,
    skip: !hasAuth,
    fetchPolicy: "cache-and-network",
  });

  /* ------------------------------------------------------------------ */
  /*  fatal token detector – *after* the latest request finishes         */
  /* ------------------------------------------------------------------ */
  React.useEffect(() => {
    if (loading || !error) return; // <-- guard 👈

    if (/getNullableType/i.test(error.message)) {
      setFatal(error);
      localStorage.removeItem("GATSBY_CRAFT_API_EXPORT_TOKEN");
      setAuth(false);
      if (tokenRef.current) tokenRef.current.value = "";
      setHasText(false);
      navigate("/importexport", { replace: true });
    }
  }, [error, loading]); // add loading dep 👈

  /* ------------------------------------------------------------------ */
  /*  helpers                                                           */
  /* ------------------------------------------------------------------ */
  function saveToken() {
    const val = tokenRef.current?.value?.trim();
    if (!val) return;
    localStorage.setItem("GATSBY_CRAFT_API_EXPORT_TOKEN", val);
    setAuth(true);
    navigate(SELECT_PATH);
  }

  const Backdrop = ({ children }) => (
    <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh" }}>{children}</Box>
  );

  /* ---------------- AUTH FORM ---------------- */
  if (!hasAuth) {
    return (
      <Backdrop>
        <ImportHeader
          title="DigiGuide Advanced Import & Export"
          subHeader="Enter Authentication Token"
        />
        <Paper elevation={2} sx={{ p: 4, maxWidth: 480, mx: "auto" }}>
          {fatalErr && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: "#ffebee",
                border: "1px solid #f44336",
                borderRadius: 1,
                color: "#c62828",
                fontSize: 14,
              }}
            >
              <strong>Failed to load manuals:</strong>
              <br />
              {fatalErr.message}
            </Box>
          )}
          <Stack spacing={3}>
            <Text tag="p">
              Enter a Craft API token with <b>read&nbsp;+&nbsp;write</b> scope.
            </Text>

            <Input
              inputRef={tokenRef}
              autoFocus
              fullWidth
              placeholder="Craft auth token"
              defaultValue={stored || qsToken}
              onChange={e => setHasText(Boolean(e.target.value.trim()))}
            />

            <Button variant="contained" onClick={saveToken} disabled={!hasText}>
              Set Token
            </Button>
          </Stack>
        </Paper>
      </Backdrop>
    );
  }

  /* ---------------- LOADING ---------------- */
  if (loading) {
    return (
      <Backdrop>
        <ImportHeader title="DigiGuide Import / Export" subHeader="Loading…" />
        <Paper
          elevation={2}
          sx={{ p: 4, maxWidth: 480, mx: "auto", textAlign: "center" }}
        >
          <CircularProgress size={48} />
        </Paper>
      </Backdrop>
    );
  }

  /* ---------------- non‑fatal error ---------------- */
  if (error) {
    return (
      <Backdrop>
        <ImportHeader title="DigiGuide Import / Export" subHeader="Error" />
        <Paper elevation={2} sx={{ p: 4, maxWidth: 640, mx: "auto" }}>
          <Text
            tag="p"
            use="span"
            options={{ size: "text-lg" }}
            style={{ color: "red" }}
          >
            Failed to load manuals: {error.message}
          </Text>
        </Paper>
      </Backdrop>
    );
  }

  /* ---------------- SELECTOR ---------------- */
  const manuals = data?.products ?? [];

  return (
    <Backdrop>
      <ImportHeader
        title="DigiGuide Advanced Import & Export"
        subHeader="Select a Manual & Language"
      />
      <Paper elevation={2} sx={{ p: 4, maxWidth: 720, mx: "auto" }}>
        <ManualSelector
          manuals={manuals}
          prevPosition={localStorage.getItem("PREV_POSITION")}
        />
      </Paper>
    </Backdrop>
  );
}
